import { useCallback, useEffect, useSyncExternalStore } from "react"

export type UIScale = 80 | 90 | 100 | 110

const STORAGE_KEY = "ui-scale"
const DEFAULT_SCALE: UIScale = 100
const VALID_SCALES: readonly UIScale[] = [80, 90, 100, 110]

function parseScale(value: string | null): UIScale {
  const parsed = Number(value)
  return VALID_SCALES.includes(parsed as UIScale)
    ? (parsed as UIScale)
    : DEFAULT_SCALE
}

function getSnapshot(): UIScale {
  return parseScale(localStorage.getItem(STORAGE_KEY))
}

function subscribe(callback: () => void): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key === STORAGE_KEY) callback()
  }
  window.addEventListener("storage", handler)
  return () => window.removeEventListener("storage", handler)
}

/**
 * Expose the UI scale as the `--ui-scale` CSS variable on the document root.
 * The actual `zoom` is applied by a media-query'd rule in index.html that only
 * takes effect at >=1024px — so desktops scale (and portaled content in
 * document.body scales with them), while phones/small tablets render natively
 * at 100% and keep correct mobile width fit.
 */
function applyScale(scale: UIScale): void {
  document.documentElement.style.setProperty("--ui-scale", String(scale / 100))
}

/**
 * Read the persisted UI scale and apply it immediately. Call once at startup,
 * before React renders, so there is no flash of unscaled content.
 */
export function initUIScale(): void {
  applyScale(getSnapshot())
}

/**
 * Hook to manage the global UI scale preference. The value is applied as a CSS
 * zoom on the document root and persisted in localStorage. Defaults to 90% so
 * the app fits a 1080p screen out of the box.
 */
export function useUIScale() {
  const scale = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => DEFAULT_SCALE,
  )

  useEffect(() => {
    applyScale(scale)
  }, [scale])

  const setScale = useCallback((newScale: UIScale) => {
    localStorage.setItem(STORAGE_KEY, String(newScale))
    applyScale(newScale)
    // Force re-render across all components using this hook
    window.dispatchEvent(
      new StorageEvent("storage", {
        key: STORAGE_KEY,
        newValue: String(newScale),
      }),
    )
  }, [])

  return { scale, setScale }
}
